import { FluidMenuItem, FluidMenuState } from '@lmig/fluid-core';

// ================================================================ //
// -- Header State Management Utilities (Public)

/**
 * Initializes derived state that includes a deep copy of the "links"
 * array, the currently selected menu item, a flattened array of all
 * menu items that contain a submenu / mega menu, & the parent
 * FluidMenuItem of the currently selected menu item (if the selected
 * item is nested within a submenu / mega menu).
 * @param links - original array of menu items from "links" Prop
 */
export const initializeHeaderState = (
  links: FluidMenuItem[]
): FluidMenuState => {
  if (!links?.length) return;

  const menuItems = cloneHeaderMenuItems(links);
  const submenuItems = initializeSubmenus(menuItems);
  const selectedItem = getSelectedItem(menuItems);

  return {
    menuItems: menuItems,
    submenuItems: submenuItems,
    selectedItem: selectedItem,
    selectedItemParent: getSelectedItemParent(submenuItems, selectedItem),
  };
};

/**
 * Recursive function that returns a deep copy of the provided
 * FluidMenuItem array (including all submenus / mega menus).
 * @param items - array of FluidMenuItem objects to be cloned
 */
export const cloneHeaderMenuItems = (
  items: FluidMenuItem[]
): FluidMenuItem[] => {
  return items.map((item) => ({
    ...item,
    ...(hasHeaderSubmenu(item) && {
      submenu: cloneHeaderMenuItems(item.submenu),
    }),
    ...(hasMegaMenu(item) && {
      megaMenu: cloneHeaderMenuItems(item.megaMenu),
    }),
  }));
};

/**
 * Returns true if the given menu item contains a submenu, false if not.
 * @param menuItem - the menu item to check for submenu array
 */
export const hasHeaderSubmenu = (menuItem: FluidMenuItem): boolean => {
  return !!menuItem?.submenu?.length;
};

/**
 * Returns true if the given menu item contains a mega menu, false if not.
 * @param menuItem - the menu item to check for mega menu array
 */
export const hasMegaMenu = (menuItem: FluidMenuItem): boolean => {
  return !!menuItem?.megaMenu?.length;
};

/**
 * Searches "_headerState.submenuItems" & returns the currently
 * selected link's parent (if the selected link is nested within a
 * submenu or mega menu). Used to style the parent element to
 * indicate that the closed submenu contains the currently selected item.
 * @param submenuItems - _headerState.submenuItems
 * @param selectedItem - the currently selected item, or null if none defined
 */
export const getSelectedItemParent = (
  submenuItems: FluidMenuItem[],
  selectedItem: FluidMenuItem
): FluidMenuItem => {
  if (!submenuItems?.length || !selectedItem) return;

  let parent = selectedItem;
  while (!!parent && parent._submenuDepth !== 1) {
    parent = submenuItems.find(
      (item) =>
        item.submenu?.includes(parent) || item.megaMenu?.includes(parent)
    );
  }
  return parent;
};

// ================================================================ //
// -- Header State Management Utilities (Private)

/**
 * Recursive function that initializes "_headerState.submenuItems".
 * Returns a flattened array of all menu items that contain a submenu
 * OR mega menu (regardless of nested depth).
 * @param items - the current array to iterate through
 * @param depth - the current nested submenu depth
 */
const initializeSubmenus = (
  items: FluidMenuItem[],
  depth = 1
): FluidMenuItem[] => {
  return items.reduce((a: FluidMenuItem[], item: FluidMenuItem) => {
    if (!hasHeaderSubmenu(item) && !hasMegaMenu(item)) return a;
    item._submenuDepth = depth;
    return [
      ...a,
      item,
      ...initializeSubmenus(
        hasMegaMenu(item) ? item.megaMenu : item.submenu,
        depth + 1
      ),
    ];
  }, []);
};

/**
 * Recursive function that finds the currently selected menu item
 * (i.e. has property "selected: true") within "_headerState.menuItems".
 * Runs on first render & when "links" Prop is updated.
 * @param items - the current array to iterate through
 */
const getSelectedItem = (items: FluidMenuItem[]): FluidMenuItem => {
  return items.reduce((selectedItem: FluidMenuItem, item: FluidMenuItem) => {
    switch (true) {
      case !!selectedItem:
        return selectedItem;
      case item.selected:
        return item;
      case hasMegaMenu(item):
        return getSelectedItem(item.megaMenu);
      case hasHeaderSubmenu(item):
        return getSelectedItem(item.submenu);
    }
  }, undefined);
};
